import React from "react";
import {
  ConsultOrderProvider,
  useConsultOrderContext,
} from "../../contexts/ConsultOrderContext";
import { useConsultOrderLifecycle } from "../../hooks/useConsultOrderLifecycle";
import { ConsultCallerUser } from "../../types/consultOrder.types";
import CreateOrderChip from "./CreateOrderChip";
import ConsultOrderPromptModal, {
  PromptRfzoState,
} from "./ConsultOrderPromptModal";
import ConsultOrderFormModal from "./ConsultOrderFormModal";
import ConsultRfzoFormModal from "./ConsultRfzoFormModal";

// App-level, route-independent host for the ITAM-2583 consult-order flow — a
// sibling of <VideoConsultManager> in App.tsx. It NEVER lives inside
// <VideoConsultAlert> (that returns null when the list is empty and re-mounts).
//
// The provider wraps an inner host so the lifecycle hook (which reads the draft
// on submit) can consume the same context the form components write to.

/** Friendly caller display name for the chip + confirmation copy. */
export const callerDisplayName = (
  user: ConsultCallerUser | null | undefined
): string => {
  if (!user) {
    return "korisnika";
  }
  const full = `${user.firstName ?? ""} ${user.secondName ?? ""}`.trim();
  return full || user.username || "korisnika";
};

const ConsultOrderHost: React.FC = () => {
  const { prefill: ctxPrefill } = useConsultOrderContext();
  const life = useConsultOrderLifecycle();

  // The prompt/chip use the hook-local prefill (available before hydration);
  // once hydrated, the context prefill is the same object.
  const caller = life.prefill?.user ?? ctxPrefill?.user ?? null;
  const callerName = callerDisplayName(caller);

  const formOpen =
    life.phase === "FORM" ||
    life.phase === "SUBMITTING" ||
    life.phase === "SUCCESS";

  const rfzoModalOpen =
    life.phase === "RFZO_FORM" ||
    life.phase === "RFZO_SUBMITTING" ||
    life.phase === "RFZO_RESULT";

  const rfzoState: PromptRfzoState =
    life.rfzoResult == null
      ? "NONE"
      : life.rfzoResult.orderable === true
      ? "ORDERABLE"
      : "TERMINAL";

  return (
    <>
      {life.chipVisible && (
        <CreateOrderChip callerName={callerName} onOpen={life.openPrompt} />
      )}

      <ConsultOrderPromptModal
        isOpen={life.phase === "PROMPT"}
        callerName={callerName}
        cncFormVisited={life.cncFormVisited}
        rfzoFormVisited={life.rfzoFormVisited}
        rfzoState={rfzoState}
        onConfirmOrder={life.confirmPrompt}
        onConfirmRfzo={life.confirmRfzo}
        onResumeRfzo={life.resumeRfzo}
        onClose={life.dismissPrompt}
      />

      <ConsultOrderFormModal
        isOpen={formOpen}
        phase={life.phase}
        graceActive={life.graceActive}
        consultEnded={life.consultEnded}
        createdOrder={life.createdOrder}
        submitError={life.submitError}
        validationErrors={life.validationErrors}
        submitAttemptId={life.submitAttemptId}
        lineErrors={life.lineErrors}
        mode={life.rfzoOrderTherapyId != null ? "RFZO_ORDER" : "CNC"}
        rfzoResult={life.rfzoResult}
        successKind={life.successKind}
        onSubmit={life.submit}
        onClose={life.closeForm}
        onCloseSuccess={life.closeSuccess}
        onDismissError={life.dismissSubmitError}
      />

      <ConsultRfzoFormModal
        isOpen={rfzoModalOpen}
        phase={life.phase}
        graceActive={life.graceActive}
        consultEnded={life.consultEnded}
        rfzoResult={life.rfzoResult}
        rfzoResultMode={life.rfzoResultMode}
        submitError={life.rfzoSubmitError}
        validationErrors={life.rfzoValidationErrors}
        submitAttemptId={life.rfzoSubmitAttemptId}
        onSubmit={life.submitRfzo}
        onClose={life.closeRfzoForm}
        onCloseResult={life.closeRfzoResult}
        onStartOrder={life.startRfzoOrder}
        onDismissError={life.dismissRfzoSubmitError}
      />
    </>
  );
};

const ConsultOrderManager: React.FC = () => {
  return (
    <ConsultOrderProvider>
      <ConsultOrderHost />
    </ConsultOrderProvider>
  );
};

export default ConsultOrderManager;
